/* Presentation only. Dawn 16 remains the sole owner of variants and cart requests. */
(() => {
  if (customElements.get('ew-product-controller')) return;
  class EWProductController extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }
    initialize() {
      if (!this.isConnected || this.active) return;
      const root = this.closest('product-info[id^="MainProduct-"]');
      const info = root?.querySelector('.product__info-container');
      const grid = root?.querySelector('.product');
      if (!root || !info || !grid) return;
      this.active = true;
      this.root = root;
      this.info = info;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.titleNode = info.querySelector(':scope > .product__title');
      this.price = root.querySelector(`[id="price-${this.dataset.section}"]`);
      if (this.titleNode && this.price) {
        this.titleSlot = document.createComment('Edelweiss desktop title');
        this.priceSlot = document.createComment('Edelweiss desktop price');
        this.titleNode.before(this.titleSlot);
        this.price.before(this.priceSlot);
        this.mobileSummary = document.createElement('header');
        this.mobileSummary.className = 'ew-product-mobile-summary';
        this.mobileSummary.hidden = true;
        grid.before(this.mobileSummary);
        this.media = window.matchMedia('(max-width: 749px)');
        this.onMedia = () => this.syncLayout();
        this.media.addEventListener('change', this.onMedia);
        this.syncLayout();
      }
      const quantity = root.querySelector('.product-form__quantity');
      const buttons = root.querySelector('product-form .product-form__buttons');
      if (quantity && buttons && !buttons.closest('.ew-product-purchase-row')) {
        this.quantityNode = quantity;
        this.buttonsNode = buttons;
        this.quantitySlot = document.createComment('Edelweiss quantity position');
        this.buttonsSlot = document.createComment('Edelweiss purchase buttons position');
        quantity.before(this.quantitySlot);
        buttons.before(this.buttonsSlot);
        const row = document.createElement('div');
        row.className = 'ew-product-purchase-row';
        if (this.dataset.giftCard === 'true') row.classList.add('ew-product-purchase-row--inline');
        buttons.before(row);
        row.append(quantity, buttons);
        this.row = row;
        this.createdPurchaseRow = true;
      } else {
        this.row = root.querySelector('.ew-product-purchase-row');
      }
      if (this.row) {
        this.syncPurchaseHeight = () => {
          const height = Math.ceil(this.row.getBoundingClientRect().height);
          if (height <= 0) return;
          root.style.setProperty('--ew-purchase-height', `${height}px`);
          document.documentElement.style.setProperty('--ew-purchase-height', `${height}px`);
        };
        this.syncPurchaseHeight();
        if ('ResizeObserver' in window) {
          this.rowObserver = new ResizeObserver(this.syncPurchaseHeight);
          this.rowObserver.observe(this.row);
        }
      }
      this.input = root.querySelector('[data-ew-gift-input]');
      this.count = root.querySelector('[data-ew-gift-count]');
      if (this.input && this.count) {
        const sync = () => {
          let value = this.input.value.slice(0, this.input.maxLength);
          if (/[\uD800-\uDBFF]$/.test(value)) value = value.slice(0, -1);
          if (value !== this.input.value) this.input.value = value;
          this.count.textContent = `${value.length} / ${this.input.maxLength}`;
        };
        this.input.addEventListener('input', sync, options);
        this.input.addEventListener('change', sync, options);
        const form = this.input.form;
        form?.addEventListener('submit', sync, { ...options, capture: true });
        form?.addEventListener('formdata', (event) => {
          sync();
          if (!this.input.disabled) event.formData.set(this.input.name, this.input.value);
        }, options);
        sync();
      }
      this.hiddenDetailNodes = new Map();
      this.refineOriginalDescription(root);
      root.setAttribute('data-ew-product-ready', 'true');
    }
    normalizeDetailText(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[’]/g, "'")
        .replace(/:\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
    }
    hideDuplicateNode(node) {
      if (!(node instanceof HTMLElement) || node.contains(document.activeElement)) return false;
      if (!this.hiddenDetailNodes.has(node)) this.hiddenDetailNodes.set(node, node.hidden);
      node.hidden = true;
      return true;
    }
    refineOriginalDescription(root) {
      const disclosure = root.querySelector('[data-ew-original-description]');
      const content = disclosure?.querySelector(':scope > .rte');
      if (!disclosure || !content) return;

      const careValues = new Map(
        [...root.querySelectorAll('.ew-product-care > div')]
          .map((row) => [
            this.normalizeDetailText(row.querySelector('dt')?.textContent),
            this.normalizeDetailText(row.querySelector('dd')?.textContent),
          ])
          .filter(([label, value]) => label && value)
      );
      const structuredLabels = new Set(['net weight', 'shelf life', 'storage']);

      content.querySelectorAll('li').forEach((item) => {
        const strong = item.querySelector(':scope > strong');
        const label = this.normalizeDetailText(strong?.textContent);
        if (!structuredLabels.has(label) || !careValues.has(label)) return;
        const itemText = this.normalizeDetailText(item.textContent);
        const value = itemText.slice(label.length).replace(/^:\s*/, '').trim();
        if (value === careValues.get(label)) this.hideDuplicateNode(item);
      });

      const allergenContent = root.querySelector('[data-ew-allergens] .ew-product-disclosure__content');
      const allergenText = this.normalizeDetailText(allergenContent?.textContent);
      if (allergenText) {
        const allergenLabels = new Set([
          'allergen notice',
          'allergens',
          'allergen information',
        ]);
        content.querySelectorAll('p').forEach((paragraph) => {
          const strong = paragraph.querySelector(':scope > strong');
          if (!allergenLabels.has(this.normalizeDetailText(strong?.textContent))) return;
          const paragraphText = this.normalizeDetailText(paragraph.textContent);
          const labelText = this.normalizeDetailText(strong.textContent);
          const bodyText = paragraphText.slice(labelText.length).replace(/^:\s*/, '').trim();
          if (bodyText === allergenText) this.hideDuplicateNode(paragraph);
        });
      }

      const insideContent = root.querySelector('[data-ew-whats-inside] .ew-product-disclosure__content');
      const insideText = this.normalizeDetailText(insideContent?.textContent);
      if (insideText) {
        const insideHeadings = new Set(["what's included", 'what is included']);
        content.querySelectorAll('h2, h3').forEach((heading) => {
          if (!insideHeadings.has(this.normalizeDetailText(heading.textContent))) return;
          const list = heading.nextElementSibling;
          const listText = list?.matches('ul, ol') ? this.normalizeDetailText(list.textContent) : '';
          if (listText !== insideText || heading.contains(document.activeElement) || list.contains(document.activeElement)) return;
          this.hideDuplicateNode(list);
          this.hideDuplicateNode(heading);
        });
      }

      content.querySelectorAll('ul, ol').forEach((list) => {
        if (![...list.querySelectorAll(':scope > li')].some((item) => !item.hidden)) this.hideDuplicateNode(list);
      });
      const visibleContent = content.cloneNode(true);
      visibleContent.querySelectorAll('[hidden]').forEach((node) => node.remove());
      const hasMeaningfulContent = this.normalizeDetailText(visibleContent.textContent)
        || visibleContent.querySelector('a, img, video, iframe, table');
      if (!hasMeaningfulContent) this.hideDuplicateNode(disclosure);
    }
    syncLayout() {
      if (!this.mobileSummary || !this.titleNode || !this.price) return;
      if (this.media.matches) {
        this.mobileSummary.append(this.titleNode, this.price);
        this.mobileSummary.hidden = false;
      } else {
        this.titleSlot.after(this.titleNode);
        this.priceSlot.after(this.price);
        this.mobileSummary.hidden = true;
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.media?.removeEventListener('change', this.onMedia);
      this.rowObserver?.disconnect();
      // Restore real nodes even when a whole product section has been detached.
      if (this.titleSlot?.parentNode && this.titleNode) this.titleSlot.after(this.titleNode);
      if (this.priceSlot?.parentNode && this.price) this.priceSlot.after(this.price);
      if (this.quantitySlot?.parentNode && this.quantityNode) this.quantitySlot.after(this.quantityNode);
      if (this.buttonsSlot?.parentNode && this.buttonsNode) this.buttonsSlot.after(this.buttonsNode);
      if (this.createdPurchaseRow) this.row?.remove();
      this.mobileSummary?.remove();
      this.titleSlot?.remove();
      this.priceSlot?.remove();
      this.quantitySlot?.remove();
      this.buttonsSlot?.remove();
      this.hiddenDetailNodes?.forEach((wasHidden, node) => {
        node.hidden = wasHidden;
      });
      this.hiddenDetailNodes?.clear();
      this.root?.style.removeProperty('--ew-purchase-height');
      document.documentElement.style.removeProperty('--ew-purchase-height');
      this.root?.removeAttribute('data-ew-product-ready');
      this.row = null;
      this.createdPurchaseRow = false;
      this.active = false;
    }
  }
  customElements.define('ew-product-controller', EWProductController);
})();
